import type { ServicePageConfig } from '../components/servicePages/serviceConfigs.js';
import type { ExtraKeywordLandingPage } from './publicMarketingConfig.js';

export function normalizeExtraKeywordPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

export function getEnabledExtraKeywordPages(
  pages: ExtraKeywordLandingPage[] | undefined,
): ExtraKeywordLandingPage[] {
  return (pages ?? []).filter((page) => page.enabled && page.path.length > 1 && page.title.trim());
}

export function getExtraKeywordPageByPath(
  pages: ExtraKeywordLandingPage[] | undefined,
  pathname: string,
): ExtraKeywordLandingPage | undefined {
  const normalized = normalizeExtraKeywordPath(pathname);
  return getEnabledExtraKeywordPages(pages).find((page) => page.path === normalized);
}

/** Map a Super Admin keyword landing into the shared ServicePageTemplate config. */
export function extraKeywordPageToServiceConfig(page: ExtraKeywordLandingPage): ServicePageConfig {
  const slug = page.path.replace(/^\//, '');
  const h1 = page.h1.trim() || page.keyword.trim() || page.title.trim();
  const intro = page.intro.trim() || page.description.trim();
  const bullets =
    page.bullets.length > 0
      ? page.bullets
      : ['Quotes, scheduling and invoicing in one workflow', 'Mobile-friendly for field teams'];
  return {
    slug,
    serviceName: h1,
    serviceTitle: h1,
    hero: {
      eyebrow: page.keyword.trim() || 'Search landing',
      title: h1,
      description: page.description.trim() || intro,
      highlights: [
        { icon: 'Clock', text: 'Fast to set up' },
        { icon: 'Shield', text: 'Configurable per organisation' },
        { icon: 'Star', text: 'Search-intent page' },
      ],
    },
    servicesIncluded: bullets,
    areasWeServe: [],
    whyChoose: [
      {
        icon: 'Sparkles',
        title: page.keyword.trim() || 'Keyword landing',
        description: intro || page.description,
      },
      {
        icon: 'CheckCircle',
        title: 'Managed in Super Admin',
        description:
          'Title, copy and FAQs are configurable in public marketing SEO settings — not hardcoded.',
      },
    ],
    reviews: [],
    cta: {
      title: `Get started with ${h1}`,
      description: page.description.trim() || intro,
    },
    seo: {
      metaTitle: page.title.trim() || h1,
      metaDescription: page.description.trim() || intro,
      keywords: page.keywords.trim() || page.keyword,
      introParagraphs: intro ? [intro] : [page.description],
      sections: [
        {
          heading: page.keyword.trim() ? `About ${page.keyword}` : 'What this page covers',
          paragraphs: intro ? [intro] : [],
          bulletList: bullets,
        },
      ],
      conclusion: page.description.trim() || intro,
    },
    faq: page.faqs.map((faq) => ({ question: faq.question, answer: faq.answer })),
  };
}
